import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { selectFixRequestFormFieldsSettings } from '@/selectors/selectors';
import { FixRequest, FixRequestFormFieldValue } from '../../types';
import { globalMessage } from '../../utils/globalMessage';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { tryUpdateReleaseStep } from '@/actions/releaseStep';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { runeCount, getFixRequestFormFieldId } from '@/helpers';
import MarkdownEditor from '../HOCs/MarkdownEditor';

interface UpdateFixDetailsModalProps extends AppComponentProps {
    releaseId: string;
    onCancel: () => void;
    fixRequest: FixRequest;
    featureItemId: string;
    stepRunId: string;
}

const UpdateFixDetailsModal: React.FC<UpdateFixDetailsModalProps> = (props: UpdateFixDetailsModalProps) => {
    const dispatch = useDispatch();
    const appFormFields = useSelector(selectFixRequestFormFieldsSettings(props.appId));
    // Older automation-created fixes were saved without form field settings, so fall back to the app's
    const stampedFormFields = props.fixRequest.formFieldsSettings ?? [];
    const fixRequestFormFields = stampedFormFields.length > 0 ? stampedFormFields : appFormFields;

    const [isModalOpen, setIsModalOpen] = React.useState(true);

    const [form] = Form.useForm();

    const updateFixRequestDetailsLoading = useIsLoading(ReleaseStepUpdatable.UpdateFixRequest, props.fixRequest.id);
    const updateFixRequestDetailsSuccess = useUpdated(ReleaseStepUpdatable.UpdateFixRequest, props.fixRequest.id);
    const updateFixRequestDetailsError = useError(ReleaseStepUpdatable.UpdateFixRequest, props.fixRequest.id);

    React.useEffect(() => {
        if (updateFixRequestDetailsSuccess) {
            globalMessage.success('Fix details updated successfully!');
            setIsModalOpen(false);
            props.onCancel();
        }
    }, [updateFixRequestDetailsSuccess]);

    React.useEffect(() => {
        if (updateFixRequestDetailsError) {
            globalMessage.error(updateFixRequestDetailsError);
        }
    }, [updateFixRequestDetailsError]);

    // Show minCharacters errors immediately for fields that already have a too-short value
    React.useEffect(() => {
        const fieldErrors = fixRequestFormFields
            .filter((field) => {
                if (!field.minCharacters || field.minCharacters <= 0) return false;
                const val = initialValues[`customField_${getFixRequestFormFieldId(field)}`];
                return typeof val === 'string' && val.length > 0 && runeCount(val) < field.minCharacters;
            })
            .map((field) => {
                const val = initialValues[`customField_${getFixRequestFormFieldId(field)}`] as string;
                return {
                    name: `customField_${getFixRequestFormFieldId(field)}`,
                    errors: [`${field.name} is too short (${runeCount(val)}/${field.minCharacters})`],
                };
            });
        if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
        }
    }, []);

    const handleOnSubmit = (values) => {
        const formFieldsValues: FixRequestFormFieldValue[] = fixRequestFormFields.map((field) => ({
            fieldId: getFixRequestFormFieldId(field),
            value: values[`customField_${getFixRequestFormFieldId(field)}`] ?? '',
        }));

        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.UpdateFixRequest,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    frItemId: props.featureItemId,
                    stepRunId: props.stepRunId,
                },
                {
                    fixId: props.fixRequest.id,
                    formFieldsValues,
                },
                props.fixRequest.id,
            ),
        );
    };

    const existingValues = props.fixRequest.formFieldsValues ?? [];

    const initialValues = fixRequestFormFields.reduce(
        (acc, field) => {
            const existing = existingValues.find((v) => v.fieldId === getFixRequestFormFieldId(field));
            acc[`customField_${getFixRequestFormFieldId(field)}`] = existing?.value ?? '';
            return acc;
        },
        {} as Record<string, string>,
    );

    return (
        <Modal
            title={'Edit fix details'}
            okText={'Update'}
            open={isModalOpen}
            onCancel={() => {
                setIsModalOpen(false);
                props.onCancel();
            }}
            destroyOnClose={true}
            onOk={() => {
                form.submit();
            }}
            okButtonProps={{ loading: updateFixRequestDetailsLoading }}
        >
            <Form form={form} onFinish={handleOnSubmit} initialValues={initialValues} layout="vertical">
                {fixRequestFormFields.map((field, index) => (
                    <Form.Item
                        key={getFixRequestFormFieldId(field)}
                        name={`customField_${getFixRequestFormFieldId(field)}`}
                        label={field.name}
                        rules={[
                            { required: field.required, message: `${field.name} is required` },
                            {
                                validator: (_, value) => {
                                    if (
                                        field.minCharacters &&
                                        field.minCharacters > 0 &&
                                        value &&
                                        runeCount(value) < field.minCharacters
                                    ) {
                                        return Promise.reject(
                                            new Error(
                                                `${field.name} is too short (${runeCount(value)}/${field.minCharacters})`,
                                            ),
                                        );
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                        style={{ marginTop: index === 0 ? 0 : 8 }}
                    >
                        <MarkdownEditor
                            preview="edit"
                            textareaProps={{ placeholder: field.placeholder || undefined }}
                        />
                    </Form.Item>
                ))}
            </Form>
        </Modal>
    );
};

export default UpdateFixDetailsModal;
